import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { AlertCircle, Check } from "lucide-react"
import {
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth"
import { useToast } from "@/hooks/use-toast"

const UserPasswordUpdate = ({ isEditing }: { isEditing: boolean }) => {
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const { toast } = useToast()
  const auth = getAuth()

  // Password strength requirements
  const minLength = 8
  const hasUppercase = /[A-Z]/.test(newPassword)
  const hasLowercase = /[a-z]/.test(newPassword)
  const hasNumber = /[0-9]/.test(newPassword)
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
  const isLongEnough = newPassword.length >= minLength
  const passwordsMatch = newPassword === confirmPassword

  const isPasswordValid =
    isLongEnough &&
    hasUppercase &&
    hasLowercase &&
    hasNumber &&
    hasSpecialChar &&
    passwordsMatch

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("All fields are required")
      return
    }

    if (!passwordsMatch) {
      setError("New passwords do not match")
      return
    }

    if (!isPasswordValid) {
      setError("Password does not meet strength requirements")
      return
    }

    setIsSubmitting(true)

    try {
      const user = auth.currentUser
      if (!user || !user.email) {
        throw new Error("User not authenticated")
      }

      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      )
      await reauthenticateWithCredential(user, credential)

      // Update password
      await updatePassword(user, newPassword)

      // Clear form and show success
      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")

      toast({
        title: "Success",
        description: "Your password has been updated.",
        variant: "default",
      })
    } catch (error: any) {
      console.error("Error updating password:", error)

      // Handle specific Firebase auth errors
      if (error.code === "auth/wrong-password") {
        setError("Current password is incorrect")
      } else if (error.code === "auth/weak-password") {
        setError("New password is too weak")
      } else if (error.code === "auth/requires-recent-login") {
        setError(
          "For security reasons, please log out and log back in before changing your password"
        )
      } else {
        setError(error.message || "Failed to update password")
      }

      toast({
        title: "Error",
        description: "Failed to update password. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
    <div className="flex items-center gap-2">
      {met ? (
        <Check size={16} className="text-green-500" />
      ) : (
        <AlertCircle size={16} className="text-gray-400" />
      )}
      <span className={met ? "text-green-600" : "text-gray-500"}>{text}</span>
    </div>
  )

  if (!isEditing) {
    return null
  }

  return (
    <Card className="mt-6 border border-gray-100 rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Update Password</CardTitle>
        <CardDescription>Change your account password</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleUpdatePassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">Current Password</Label>
            <Input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password"
              error={!!error && error.includes("Current password")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              error={!!error && error.includes("New password")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              error={!!error && !passwordsMatch}
            />
            {newPassword && confirmPassword && !passwordsMatch && (
              <p className="text-sm text-red-500 mt-1">
                Passwords do not match
              </p>
            )}
          </div>

          {newPassword && (
            <div className="bg-gray-50 p-3 rounded mt-2 space-y-1">
              <p className="text-sm font-medium mb-1">Password requirements:</p>
              <RequirementItem
                met={isLongEnough}
                text={`At least ${minLength} characters`}
              />
              <RequirementItem
                met={hasUppercase}
                text="At least one uppercase letter"
              />
              <RequirementItem
                met={hasLowercase}
                text="At least one lowercase letter"
              />
              <RequirementItem met={hasNumber} text="At least one number" />
              <RequirementItem
                met={hasSpecialChar}
                text="At least one special character"
              />
            </div>
          )}

          {error && <p className="text-sm text-red-500">{error}</p>}

          <Button
            type="submit"
            disabled={
              isSubmitting ||
              !currentPassword ||
              !isPasswordValid ||
              !confirmPassword
            }
            className="w-full mt-2"
          >
            {isSubmitting ? "Updating..." : "Update Password"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export default UserPasswordUpdate
